<script setup lang="ts">
import { useNuxtApp } from '#app'
import FullProduct from '~/models/Product/FullProduct'
import DataLayer from '~/extensions/DataLayer'
import PharmacyProductAvailability from '~/models/Api/Pharmacy/PharmacyProductAvailability'

const modalDeliveryVisible = ref(false)
const modalPharmacyVisible = ref(false)

const props = defineProps({
    productData: {
        type: Object as PropType<FullProduct>,
        required: true,
    },
})

const openDeliveryModal = () => {
    if (!props.productData.availabilityModalEnabled) {
        return
    }

    useNuxtApp().$bus.$emit('showModal', 'modal-deliveries')
    modalDeliveryVisible.value = true
}

const allPharmaciesEmpty = ref(true)
const pharmacies = ref<PharmacyProductAvailability[]>([])
const pharmacyText = ref<string | null>(null)

const openPharmacyModal = async () => {
    const response = await useNuxtApp().$repository.getPharmacyProductsAvailability(props.productData.id)
    pharmacies.value = response.pharmacyProductAvailability
    pharmacyText.value = response.stockValidText
    allPharmaciesEmpty.value = response.allEmpty

    if (props.productData.pharmacyPickupAvailabilityText.length === 0 || allPharmaciesEmpty.value) {
        return
    }

    useNuxtApp().$bus.$emit('showModal', 'modal-pharmacies')
    modalPharmacyVisible.value = true

    DataLayer.push({
        event: 'pharmacy_availability_open',
    })
}

const config = useRuntimeConfig()

const showPriceInMobileApp =
    config.public.migrateToMobileApp &&
    (props.productData.mobileAppPrice || props.productData.mobileAppPriceForLoggedInUser || props.productData.hasMobileAppAction)
</script>

<template>
    <div v-if="productData.status === 1" class="service-detail__basket-box">
        <div>
            <div v-if="productData.service && productData.service.validTill" class="text-success fwb mb-2 fs-14 text-left">
                {{ $t('vouchers.validTill') }} {{ productData.service.validTill }}
            </div>

            <img v-if="productData.mobileBanner" :src="productData.mobileBanner" loading="lazy" class="d-block d-md-none mt-3" />

            <ObjectsXmasDelivery
                v-if="productData.xmasDelivery?.isEnabled"
                :guarantee="productData.xmasDelivery?.isGuaranteed"
                :text="productData.xmasDelivery?.message"
            />

            <Modal
                modal-class="modal-deliveries"
                modal-name="modal-deliveries"
                :title="$t('deliveries.title')"
                @hidden="modalDeliveryVisible = false"
            >
                <ObjectsDelivery :product-id="productData.id" />
            </Modal>

            <Modal
                modal-class="modal-pharmacies"
                modal-name="modal-pharmacies"
                size="xl"
                :title="$t('pharmacy.availabilityProducts')"
                @hidden="modalPharmacyVisible = false"
            >
                <ObjectsPharmacy :product-id="productData.id" :pharmacies="pharmacies" :pharmacy-text="pharmacyText || ''" />
            </Modal>

            <!--            <UiCashback v-if="productData.cashback" :cashback="productData.cashback" />-->

            <div v-if="productData.hasIdleSellingSettingsStatus" class="idle-warning">{{ $t('idleWarning') }}</div>

            <ElementsPriceWithCode
                v-if="productData.productCodeSale?.hasCodeSale"
                :product="productData"
                :product-code="productData.productCodeSale"
            />

            <ElementsPremiumPrice :product="productData" />

            <ElementsPriceInMobileApp v-if="showPriceInMobileApp" :product="productData" />

            <LayoutProductDetailPricingAndAddToBasket :product-data="productData" />
            <LayoutProductDetailPricingAndAddToBasket v-if="$device.isMobile" :fixed-position="true" :product-data="productData" />

            <div
                v-if="productData.isPreorder"
                class="block-availability noStock"
                v-html="$t('productDetail.preorder.availabilityText', { deliveryDate: productData.fastestDeliveryDateSimple })"
            ></div>
            <div
                v-else-if="productData.isAvailable"
                class="block-availability"
                :class="{ noStock: !productData.amountInStock, 'block-availability--clickable': productData.availabilityModalEnabled }"
                @click="openDeliveryModal"
            >
                <div class="block-availability__fastest">
                    <div class="fastestdelivery-date" style="display: inline">
                        <span v-html="productData.fastestDeliveryDate"></span>&nbsp;
                        <span
                            v-if="productData.availabilityModalEnabled"
                            class="me-3 delivery-term fastestdelivery-date__link"
                            v-html="$t('productDetail.moreInfo')"
                        ></span>
                    </div>
                    <div v-if="productData.amountInStock > 5" class="stock" :class="`text-success`">
                        {{ $t('productDetail.inStock') }}
                    </div>
                    <div v-else-if="productData.amountInStock > 0" class="stock" :class="`text-success`">
                        {{ $t('productDetail.inStockCount', productData.amountInStock) }}
                    </div>
                    <div v-else class="stock" :class="`text-warning`">
                        {{ $t('productDetail.noStock') }}
                    </div>
                </div>
                <div v-if="productData?.carPickupAvailabilityText.length > 0" class="block-availability__pilulka-logistics fs-12 mt-2">
                    {{ productData.carPickupAvailabilityText }}
                </div>
                <div v-if="productData?.boxPickupAvailabilityText.length > 0" class="block-availability__pilulka-logistics fs-12">
                    {{ productData.boxPickupAvailabilityText }}
                </div>
                <div
                    v-if="productData?.pharmacyPickupAvailabilityText.length > 0"
                    class="block-availability__pilulka-logistics fs-12"
                    @click.stop="openPharmacyModal()"
                    v-html="productData.pharmacyPickupAvailabilityText"
                ></div>
            </div>
            <div v-else class="block-availability">{{ $t('productDetail.outStock') }}</div>

            <ElementsBoxMessage
                v-if="!showPriceInMobileApp && productData.mobileAppPrice && productData.mobileAppPrice < productData.price"
                class="mt-3"
                :text="$t('product.inPilulkaAppFor', { price: $formatCurrency(productData.mobileAppPrice) })"
                type="primary"
                :button-text="''"
            />
            <!--            $t('product.downloadApp')-->
        </div>
    </div>

    <div v-else class="service-detail__notPurchasable">{{ $t('productDetail.notPurchasable') }}</div>
</template>

<style lang="scss" scoped>
.service-detail {
    &__notPurchasable {
        background: $color-alert-light;
        color: $color-alert;
        text-align: center;
        padding: 9px;
        border-radius: 10px;
    }
}

.idle-warning {
    border-radius: $border-radius;
    background-color: $color-green-light;
    color: $color-green-dark;
    padding: 8px 1em;
    margin: 0 0 1rem 0;
    font-size: 13px;
}
</style>
